import React, { useState, useEffect, useRef } from "react";
import { db } from "@/api/db";
import { Briefcase, ChevronRight, Search, ChevronDown, X, DollarSign, Globe, Tag } from "lucide-react";


// ─── Constants ────────────────────────────────────────────────────────────────

const AREAS      = ["Todas", "Tecnologia", "Marketing", "Vendas", "Dados", "Produto", "Design", "Comunidade"];
const SENIORITIES = ["Todos", "Júnior", "Pleno", "Sênior", "Liderança", "Não especificado"];
const COUNTRIES  = ["Todos", "Brasil", "Portugal", "Global"];
const LANGUAGES  = ["Todos", "Português", "Inglês"];

const FLAG_EMOJI = { Brasil: "🇧🇷", Portugal: "🇵🇹", Global: "🌎" };

const SOURCE_STYLE = {
  Remotive:     { background: "#D5F9D7", color: "#008572" },
  WWR:          { background: "#FEEDB4", color: "#65541B" },
  Gupy:         { background: "#E8F4FD", color: "#0066CC" },
  VagasRemotas: { background: "#FEE8E0", color: "#C0392B" },
  GetOnBoard:   { background: "#F0E8FD", color: "#6C3483" },
  Greenhouse:   { background: "#E6F9EE", color: "#1D6B34" },
  Lever:        { background: "#FFF4E0", color: "#B45309" },
  RemoteOK:     { background: "#EDE9FE", color: "#5B21B6" },
};

const SOURCE_DISPLAY = {
  RemoteOK: "Remote OK",
};

const AVATAR_COLORS = ["#00664C", "#008572", "#00DCA4", "#65541B", "#892E38"];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function avatarColor(name) {
  return AVATAR_COLORS[(name?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];
}

function formatSalary(min, max, currency) {
  if (!min && !max) return null;
  const sym = currency === "BRL" ? "R$" : currency === "USD" ? "$" : "€";
  const fmt = (n) => `${sym}${(n / 1000).toFixed(0)}k`;
  if (min && max) return `${fmt(min)}–${fmt(max)}`;
  if (min) return `${fmt(min)}+`;
  return `até ${fmt(max)}`;
}

// ─── Dropdown genérico ────────────────────────────────────────────────────────

function SelectDropdown({ label, value, options, onChange, renderOption }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const isDefault = value === options[0];

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium whitespace-nowrap"
        style={{
          border: isDefault ? "1px solid #E5E9EA" : "1px solid #00664C",
          background: isDefault ? "#fff" : "#EAFAF6",
          color: isDefault ? "#646C6F" : "#00664C",
        }}
      >
        {isDefault ? label : (renderOption ? renderOption(value) : value)}
        <ChevronDown className="w-3.5 h-3.5 opacity-50"
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
      </button>

      {open && (
        <div
          className="absolute top-full mt-1 left-0 z-50 rounded-2xl py-1.5 min-w-[160px]"
          style={{ background: "#fff", border: "1px solid #E5E9EA", boxShadow: "0 8px 24px rgba(0,0,0,0.10)" }}
        >
          {options.map(opt => (
            <button
              key={opt}
              onClick={() => { onChange(opt); setOpen(false); }}
              className="w-full text-left px-4 py-2 text-sm"
              style={{
                color: opt === value ? "#00664C" : "#2E3233",
                background: opt === value ? "#EAFAF6" : "transparent",
                fontWeight: opt === value ? 600 : 400,
              }}
              onMouseEnter={e => { if (opt !== value) e.currentTarget.style.background = "#F2F4F4"; }}
              onMouseLeave={e => { if (opt !== value) e.currentTarget.style.background = "transparent"; }}
            >
              {renderOption ? renderOption(opt) : opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Filtro de salário (range) ────────────────────────────────────────────────

function SalaryDropdown({ salaryMin, salaryMax, onChangeMin, onChangeMax }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const hasFilter = salaryMin !== null || salaryMax !== null;
  const fmtK = (v) => v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${v}`;
  const btnLabel = hasFilter
    ? `${fmtK(salaryMin ?? 0)} – ${salaryMax !== null ? fmtK(salaryMax) : "∞"}`
    : "Salário";

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium whitespace-nowrap"
        style={{
          border: hasFilter ? "1px solid #00664C" : "1px solid #E5E9EA",
          background: hasFilter ? "#EAFAF6" : "#fff",
          color: hasFilter ? "#00664C" : "#646C6F",
        }}
      >
        {btnLabel}
        <ChevronDown className="w-3.5 h-3.5 opacity-50"
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
      </button>

      {open && (
        <div
          className="absolute top-full mt-1 right-0 z-50 rounded-2xl p-4 w-60"
          style={{ background: "#fff", border: "1px solid #E5E9EA", boxShadow: "0 8px 24px rgba(0,0,0,0.10)" }}
        >
          <p className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: "#99A6AB" }}>
            Faixa salarial (R$)
          </p>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-xs block mb-1" style={{ color: "#646C6F" }}>Mínimo</label>
              <input
                type="number" min={0} step={500} placeholder="Ex: 3000"
                value={salaryMin ?? ""}
                onChange={e => onChangeMin(e.target.value !== "" ? Number(e.target.value) : null)}
                className="w-full px-3 py-2 rounded-xl text-sm"
                style={{ border: "1px solid #E5E9EA", outline: "none", color: "#2E3233" }}
              />
            </div>
            <span className="pb-2 text-sm" style={{ color: "#C8CED0" }}>–</span>
            <div className="flex-1">
              <label className="text-xs block mb-1" style={{ color: "#646C6F" }}>Máximo</label>
              <input
                type="number" min={0} step={500} placeholder="Ex: 15000"
                value={salaryMax ?? ""}
                onChange={e => onChangeMax(e.target.value !== "" ? Number(e.target.value) : null)}
                className="w-full px-3 py-2 rounded-xl text-sm"
                style={{ border: "1px solid #E5E9EA", outline: "none", color: "#2E3233" }}
              />
            </div>
          </div>
          {hasFilter && (
            <button
              onClick={() => { onChangeMin(null); onChangeMax(null); }}
              className="mt-3 text-xs w-full text-center py-1 rounded-lg"
              style={{ color: "#99A6AB", background: "#F2F4F4" }}
            >
              Limpar
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function CompanyAvatar({ logo, name }) {
  const [imgError, setImgError] = useState(false);
  const initial = name?.[0]?.toUpperCase() ?? "?";

  if (logo && !imgError) {
    return (
      <img src={logo} alt={name} onError={() => setImgError(true)}
        className="w-12 h-12 rounded-full object-contain bg-white flex-shrink-0"
        style={{ border: "1px solid #E5E9EA" }} />
    );
  }
  return (
    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
      style={{ background: avatarColor(name) }}>
      {initial}
    </div>
  );
}

// ─── Card de vaga ─────────────────────────────────────────────────────────────

function JobCard({ job }) {
  const salary      = formatSalary(job.salary_min, job.salary_max, job.salary_currency);
  const tags        = Array.isArray(job.tags) ? job.tags : [];
  const sourceStyle = SOURCE_STYLE[job.source] ?? { background: "#E5E9EA", color: "#646C6F" };

  return (
    <div className="flex flex-col bg-white rounded-2xl transition-shadow hover:shadow-md"
      style={{
        border: job.is_featured ? "1.5px solid #00DCA4" : "1px solid #E8ECEF",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        height: "100%",
      }}
    >
      {/* Corpo */}
      <div className="flex flex-col gap-3 p-5 flex-1">

        {/* Topo: logo + título + empresa */}
        <div className="flex gap-3 items-start">
          <CompanyAvatar logo={job.company_logo_url} name={job.company} />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-bold text-sm leading-snug"
                style={{ color: "#2E3233", fontFamily: "Poppins, sans-serif" }}>
                {job.title}
              </h3>
              {job.is_featured && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                  style={{ background: "#00DCA4", color: "#003326" }}>
                  ⭐
                </span>
              )}
            </div>
            <p className="text-xs mt-0.5" style={{ color: "#99A6AB" }}>{job.company}</p>
          </div>
        </div>

        {/* Linhas de info */}
        <div className="flex flex-col gap-2">

          {/* Salário */}
          <div className="flex items-center gap-2">
            <DollarSign className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#C8CED0" }} />
            <span className="text-xs" style={{ color: salary ? "#2E3233" : "#C8CED0", fontWeight: salary ? 600 : 400 }}>
              {salary ?? "Salário não informado"}
            </span>
          </div>

          {/* Modalidade + nível */}
          <div className="flex items-center gap-2">
            <Globe className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#C8CED0" }} />
            <span className="text-xs" style={{ color: "#2E3233" }}>Remoto</span>
            {job.seniority && job.seniority !== "Não especificado" && (
              <>
                <span className="text-xs" style={{ color: "#C8CED0" }}>·</span>
                <span className="text-xs" style={{ color: "#646C6F" }}>{job.seniority}</span>
              </>
            )}
          </div>

          {/* Área + idioma + fonte */}
          <div className="flex items-center gap-2 flex-wrap">
            <Briefcase className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#C8CED0" }} />
            <span className="text-xs" style={{ color: "#646C6F" }}>{job.area ?? "Geral"}</span>
            <span className="text-xs">{job.language === "Inglês" ? "🇬🇧" : "🇧🇷"}</span>
            {job.source && (
              <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold" style={sourceStyle}>
                {SOURCE_DISPLAY[job.source] ?? job.source}
              </span>
            )}
          </div>
        </div>

        {/* Descrição */}
        {job.description && (
          <p className="text-xs line-clamp-2 leading-relaxed" style={{ color: "#99A6AB" }}>
            {job.description}
          </p>
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {tags.slice(0, 2).map(tag => (
              <span key={tag} className="text-xs px-2 py-0.5 rounded-full"
                style={{ background: "#F2F4F4", color: "#646C6F" }}>
                {tag}
              </span>
            ))}
            {tags.length > 2 && (
              <span className="text-xs" style={{ color: "#C8CED0" }}>+{tags.length - 2}</span>
            )}
          </div>
        )}
      </div>

      {/* Rodapé: link centralizado */}
      <div className="px-5 py-3 flex justify-center"
        style={{ borderTop: "1px solid #F0F2F3" }}>
        <a
          href={job.apply_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs font-semibold transition-opacity hover:opacity-70"
          style={{ color: "#00664C" }}
        >
          Acessar detalhes <ChevronRight className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl p-5 bg-white animate-pulse" style={{ border: "1px solid #E5E9EA" }}>
      <div className="flex justify-between mb-4">
        <div className="w-12 h-12 rounded-full" style={{ background: "#E5E9EA" }} />
        <div className="w-16 h-5 rounded-full" style={{ background: "#E5E9EA" }} />
      </div>
      <div className="space-y-2 mb-4">
        <div className="h-4 rounded-full w-3/4" style={{ background: "#E5E9EA" }} />
        <div className="h-3 rounded-full w-1/2" style={{ background: "#E5E9EA" }} />
        <div className="h-3 rounded-full w-1/3" style={{ background: "#E5E9EA" }} />
      </div>
      <div className="h-3 rounded-full w-full" style={{ background: "#E5E9EA" }} />
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function VagasRemotas() {
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [filters, setFilters] = useState({
    country: "Todos", area: "Todas", seniority: "Todos", language: "Todos",
  });
  const [salaryMin, setSalaryMin] = useState(null);
  const [salaryMax, setSalaryMax] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await db.entities.JobListing.filter({ is_published: true }, "-published_at");
        const nonPortus = (Array.isArray(data) ? data : []).filter(j => j.source !== "Portus");
        setJobs(nonPortus);
      } catch (err) {
        console.error("VagasRemotas load error:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function setFilter(key, value) {
    setFilters(prev => ({ ...prev, [key]: value }));
  }

  const filtered = jobs.filter(job => {
    if (search) {
      const q = search.toLowerCase();
      if (!job.title?.toLowerCase().includes(q) && !job.company?.toLowerCase().includes(q)) return false;
    }
    if (filters.country !== "Todos"  && job.country   !== filters.country)   return false;
    if (filters.area !== "Todas"     && job.area       !== filters.area)      return false;
    if (filters.seniority !== "Todos" && job.seniority !== filters.seniority) return false;
    if (filters.language !== "Todos" && job.language   !== filters.language)  return false;
    // Salário mínimo: salary_max do job deve ser >= filtro
    if (salaryMin !== null) {
      const ref = job.salary_max || job.salary_min;
      if (!ref || ref < salaryMin) return false;
    }
    // Salário máximo: salary_min do job deve ser <= filtro
    if (salaryMax !== null) {
      const ref = job.salary_min || job.salary_max;
      if (!ref || ref > salaryMax) return false;
    }
    return true;
  });

  const sorted = [
    ...filtered.filter(j => j.is_featured),
    ...filtered.filter(j => !j.is_featured),
  ];

  const hasActiveFilters =
    search ||
    filters.country !== "Todos" || filters.area !== "Todas" ||
    filters.seniority !== "Todos" || filters.language !== "Todos" ||
    salaryMin !== null || salaryMax !== null;

  function clearAll() {
    setSearch("");
    setFilters({ country: "Todos", area: "Todas", seniority: "Todos", language: "Todos" });
    setSalaryMin(null);
    setSalaryMax(null);
  }

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
          Vagas Globais
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "#B0FCEA", color: "#003326" }}>Beta</span>
        </h1>
        <p className="text-sm mt-0.5" style={{ color: "#99A6AB" }}>
          Oportunidades globais atualizadas diariamente
        </p>
      </div>

      {/* Barra de filtros */}
      <div className="rounded-2xl p-3 bg-white" style={{ border: "1px solid #E5E9EA" }}>
        <div className="flex flex-wrap items-center gap-2">
          {/* Busca */}
          <div
            className="flex items-center gap-2 flex-1 min-w-[180px] px-3 py-2 rounded-xl"
            style={{ border: "1px solid #E5E9EA" }}
          >
            <Search className="w-4 h-4 flex-shrink-0" style={{ color: "#99A6AB" }} />
            <input
              type="text"
              placeholder="Buscar por cargo ou empresa..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-1 text-sm bg-transparent outline-none"
              style={{ color: "#2E3233" }}
            />
            {search && (
              <button onClick={() => setSearch("")}>
                <X className="w-3.5 h-3.5" style={{ color: "#C8CED0" }} />
              </button>
            )}
          </div>

          {/* Dropdowns */}
          <SelectDropdown label="Área" value={filters.area} options={AREAS}
            onChange={v => setFilter("area", v)} />
          <SelectDropdown label="Nível" value={filters.seniority} options={SENIORITIES}
            onChange={v => setFilter("seniority", v)} />
          <SelectDropdown label="País" value={filters.country} options={COUNTRIES}
            onChange={v => setFilter("country", v)}
            renderOption={opt => opt === "Todos" ? opt : `${FLAG_EMOJI[opt] ?? ""} ${opt}`} />
          <SelectDropdown label="Idioma" value={filters.language} options={LANGUAGES}
            onChange={v => setFilter("language", v)} />
          <SalaryDropdown
            salaryMin={salaryMin} salaryMax={salaryMax}
            onChangeMin={setSalaryMin} onChangeMax={setSalaryMax}
          />

          {hasActiveFilters && (
            <button
              onClick={clearAll}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium flex-shrink-0"
              style={{ color: "#99A6AB", border: "1px solid #E5E9EA" }}
            >
              <X className="w-3.5 h-3.5" /> Limpar
            </button>
          )}
        </div>
      </div>

      {/* Contagem */}
      {!loading && (
        <p className="text-sm px-1" style={{ color: "#99A6AB" }}>
          {sorted.length} vaga{sorted.length !== 1 ? "s" : ""} encontrada{sorted.length !== 1 ? "s" : ""}
        </p>
      )}

      {/* Grid de cards */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => <SkeletonCard key={i} />)}
        </div>
      ) : sorted.length === 0 ? (
        <div className="rounded-2xl p-12 flex flex-col items-center gap-3 text-center bg-white"
          style={{ border: "1px solid #E5E9EA" }}>
          <Briefcase className="w-10 h-10 opacity-20" style={{ color: "#00664C" }} />
          <p className="text-sm" style={{ color: "#99A6AB" }}>Nenhuma vaga encontrada com esses filtros.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sorted.map(job => <JobCard key={job.id} job={job} />)}
        </div>
      )}
    </div>
  );
}
